import { useState, useEffect } from 'react';
import { eventsAPI, clientsAPI } from '../lib/api';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, ChevronLeft, ChevronRight, Loader2, Trash2 } from 'lucide-react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, startOfWeek, endOfWeek } from 'date-fns';

export default function CalendarPage() {
  const { t } = useLanguage();
  
  const EVENT_COLORS = [
    { value: 'sage', label: t('sage'), class: 'bg-[#78866B]/20 border-l-[#78866B]' },
    { value: 'clay', label: t('clay'), class: 'bg-[#C98E75]/20 border-l-[#C98E75]' },
    { value: 'stone', label: t('stone'), class: 'bg-[#A8A29E]/20 border-l-[#A8A29E]' },
    { value: 'blue', label: t('blue'), class: 'bg-blue-100 border-l-blue-500' },
  ];

  const [events, setEvents] = useState([]);
  const [clients, setClients] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    start: '',
    end: '',
    description: '',
    client_id: '',
    color: 'sage'
  });

  const fetchData = async () => {
    try {
      const [eventsRes, clientsRes] = await Promise.all([
        eventsAPI.getAll(),
        clientsAPI.getAll()
      ]);
      setEvents(eventsRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      toast.error('Failed to fetch events');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const resetForm = () => {
    setFormData({
      title: '',
      start: '',
      end: '',
      description: '',
      client_id: '',
      color: 'sage'
    });
    setEditingEvent(null);
    setSelectedDate(null);
  };

  const openAddDialog = (date = null) => {
    resetForm();
    if (date) {
      const dateStr = format(date, 'yyyy-MM-dd');
      setFormData(prev => ({
        ...prev,
        start: `${dateStr}T09:00`,
        end: `${dateStr}T10:00`
      }));
      setSelectedDate(date);
    }
    setDialogOpen(true);
  };

  const openEditDialog = (event) => {
    setFormData({
      title: event.title || '',
      start: event.start ? event.start.slice(0, 16) : '',
      end: event.end ? event.end.slice(0, 16) : '',
      description: event.description || '',
      client_id: event.client_id || '',
      color: event.color || 'sage'
    });
    setEditingEvent(event);
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast.error('Event title is required');
      return;
    }
    if (!formData.start || !formData.end) {
      toast.error('Start and end times are required');
      return;
    }

    setSaving(true);
    try {
      const data = {
        ...formData,
        client_id: formData.client_id === 'none' ? '' : formData.client_id,
        start: formData.start.includes('T') ? formData.start : `${formData.start}T00:00`,
        end: formData.end.includes('T') ? formData.end : `${formData.end}T23:59`
      };

      if (editingEvent) {
        await eventsAPI.update(editingEvent.id, data);
        toast.success(t('eventUpdated'));
      } else {
        await eventsAPI.create(data);
        toast.success(t('eventCreated'));
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save event');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editingEvent) return;
    if (!window.confirm(`${t('delete')} "${editingEvent.title}"?`)) return;
    
    try {
      await eventsAPI.delete(editingEvent.id);
      toast.success(t('eventDeleted'));
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  const getColorClass = (color) => {
    return EVENT_COLORS.find(c => c.value === color)?.class || EVENT_COLORS[0].class;
  };

  const getEventsForDay = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return events.filter(event => {
      const eventDate = event.start?.slice(0, 10);
      return eventDate === dateStr;
    });
  };

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 0 });
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });
  const calendarDays = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-12 w-48 bg-[#E7E5E4] rounded-lg animate-pulse" />
        <div className="h-[600px] bg-[#E7E5E4] rounded-xl animate-pulse" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="calendar-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-medium text-[#292524] tracking-tight">{t('calendar')}</h1>
          <p className="text-[#78716C] mt-1">{events.length} {t('eventsScheduled')}</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              className="rounded-full bg-[#57534E] hover:bg-[#57534E]/90 h-11 px-6"
              onClick={() => openAddDialog()}
              data-testid="add-event-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('addEvent')}
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl font-medium">
                {editingEvent ? t('editEvent') : t('newEvent')}
              </DialogTitle>
              <DialogDescription className="sr-only">Event form</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="title">{t('eventTitle')} *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder={t('eventTitle')}
                  className="h-11"
                  data-testid="event-title-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="start">{t('start')} *</Label>
                  <Input
                    id="start"
                    type="datetime-local"
                    step="300"
                    value={formData.start}
                    onChange={(e) => setFormData({ ...formData, start: e.target.value })}
                    className="h-11"
                    data-testid="event-start-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="end">{t('end')} *</Label>
                  <Input
                    id="end"
                    type="datetime-local"
                    step="300"
                    value={formData.end}
                    onChange={(e) => setFormData({ ...formData, end: e.target.value })}
                    className="h-11"
                    data-testid="event-end-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="client">{t('linkToClient')}</Label>
                <Select
                  value={formData.client_id || "none"}
                  onValueChange={(value) => setFormData({ ...formData, client_id: value === "none" ? "" : value })}
                >
                  <SelectTrigger className="h-11" data-testid="event-client-select">
                    <SelectValue placeholder={t('selectClient')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('none')}</SelectItem>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="color">{t('color')}</Label>
                <Select
                  value={formData.color}
                  onValueChange={(value) => setFormData({ ...formData, color: value })}
                >
                  <SelectTrigger className="h-11" data-testid="event-color-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EVENT_COLORS.map((color) => (
                      <SelectItem key={color.value} value={color.value}>
                        <div className="flex items-center gap-2">
                          <div className={`w-3 h-3 rounded-full ${color.class.split(' ')[0]}`} />
                          {color.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">{t('description')}</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder={t('description')}
                  rows={3}
                  data-testid="event-description-input"
                />
              </div>
              <div className="flex justify-between pt-4">
                <div>
                  {editingEvent && (
                    <Button 
                      type="button" 
                      variant="ghost" 
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={handleDelete}
                      data-testid="delete-event-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      {t('delete')}
                    </Button>
                  )}
                </div>
                <div className="flex gap-3">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-full">
                    {t('cancel')}
                  </Button>
                  <Button 
                    type="submit" 
                    className="rounded-full bg-[#57534E] hover:bg-[#57534E]/90"
                    disabled={saving}
                    data-testid="save-event-btn"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    {editingEvent ? t('update') : t('create')}
                  </Button>
                </div>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Calendar Navigation */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-medium text-[#292524]">
              {format(currentDate, 'MMMM yyyy')}
            </h2>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={() => setCurrentDate(subMonths(currentDate, 1))}
                className="h-9 w-9 rounded-full"
                data-testid="prev-month-btn"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                onClick={() => setCurrentDate(new Date())}
                className="h-9 px-4 rounded-full text-sm"
                data-testid="today-btn"
              >
                {t('today')}
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setCurrentDate(addMonths(currentDate, 1))}
                className="h-9 w-9 rounded-full"
                data-testid="next-month-btn"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Calendar Grid - Desktop */}
          <div className="hidden sm:grid grid-cols-7 gap-px bg-[#E7E5E4] rounded-xl overflow-hidden">
            {/* Day headers */}
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
              <div key={day} className="bg-[#F5F5F4] p-3 text-center">
                <span className="text-sm font-medium text-[#78716C]">{day}</span>
              </div>
            ))}
            
            {/* Calendar days */}
            {calendarDays.map((day) => {
              const dayEvents = getEventsForDay(day);
              const isCurrentMonth = isSameMonth(day, currentDate);
              const isToday = isSameDay(day, new Date());
              
              return (
                <div
                  key={format(day, 'yyyy-MM-dd')}
                  className={`bg-white min-h-[100px] p-2 cursor-pointer hover:bg-[#FAFAF9] transition-colors ${
                    !isCurrentMonth ? 'opacity-40' : ''
                  }`}
                  onClick={() => openAddDialog(day)}
                  data-testid={`calendar-day-${format(day, 'yyyy-MM-dd')}`}
                >
                  <div className={`text-sm font-medium mb-1 w-7 h-7 flex items-center justify-center rounded-full ${
                    isToday ? 'bg-[#57534E] text-white' : 'text-[#292524]'
                  }`}>
                    {format(day, 'd')}
                  </div>
                  <div className="space-y-1">
                    {dayEvents.slice(0, 3).map((event) => (
                      <div
                        key={event.id}
                        className={`text-xs p-1 rounded border-l-2 truncate ${getColorClass(event.color)}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditDialog(event);
                        }}
                        data-testid={`event-${event.id}`}
                      >
                        {event.title}
                      </div>
                    ))}
                    {dayEvents.length > 3 && (
                      <div className="text-xs text-[#78716C] pl-1">
                        +{dayEvents.length - 3} {t('more')}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Mobile Calendar - Day List View */}
          <div className="sm:hidden space-y-2">
            {calendarDays
              .filter(day => isSameMonth(day, currentDate))
              .map((day) => {
                const dayEvents = getEventsForDay(day);
                const isToday = isSameDay(day, new Date());
                if (dayEvents.length === 0 && !isToday) return null;
                return (
                  <div
                    key={format(day, 'yyyy-MM-dd')}
                    className={`p-3 rounded-xl cursor-pointer ${isToday ? 'bg-[#57534E]/5 border border-[#57534E]/20' : 'bg-[#F5F5F4]/50'}`}
                    onClick={() => openAddDialog(day)}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-sm font-medium ${isToday ? 'text-[#57534E]' : 'text-[#292524]'}`}>
                        {format(day, 'EEE, MMM d')}
                      </span>
                      {isToday && <span className="text-xs bg-[#57534E] text-white px-2 py-0.5 rounded-full">Today</span>}
                    </div>
                    {dayEvents.length > 0 ? (
                      <div className="space-y-1 mt-2">
                        {dayEvents.map((event) => (
                          <div
                            key={event.id}
                            className={`text-sm p-2 rounded-lg border-l-2 ${getColorClass(event.color)}`}
                            onClick={(e) => { e.stopPropagation(); openEditDialog(event); }}
                          >
                            <span className="font-medium">{event.title}</span>
                            <span className="text-xs text-[#78716C] ml-2">{format(new Date(event.start), 'h:mm a')}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-[#78716C] mt-1">No events</p>
                    )}
                  </div>
                );
              }).filter(Boolean)}
            {calendarDays.filter(day => isSameMonth(day, currentDate) && (getEventsForDay(day).length > 0 || isSameDay(day, new Date()))).length === 0 && (
              <p className="text-center text-[#78716C] py-8">{t('noUpcomingEvents')}</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
